import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { Epic, EpicFromJSON } from "../bitmaker/schemas/Epic";
import { BitmakerAPIClient } from "../services/BitmakerApp";
import { useApiError } from "./useApiError";

type ChartType = "timeStats" | "developmentMetrics";

const getDefaultDateRange = () => {
  const from = new Date(new Date().setUTCMonth(new Date().getUTCMonth() - 11));
  const to = new Date();
  return { from, to };
};

export const useProject = (projectId: string | undefined) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [project, setProject] = useState<Epic | null>(null);
  const [loadingProject, setLoadingProject] = useState(false);
  const [fetchProjectError, setFetchProjectError] = useState<string | null>(
    null,
  );

  const [fromDate, setFromDate] = useState<Date>(
    () => getDefaultDateRange().from,
  );
  const [toDate, setToDate] = useState<Date>(() => getDefaultDateRange().to);
  const [selectedChart, setSelectedChart] = useState<ChartType>("timeStats");

  const { handleAPIError } = useApiError();
  const isMountedRef = useRef(false);

  const isChartType = (value: string | null): value is ChartType => {
    return value === "timeStats" || value === "developmentMetrics";
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchProject = useCallback(() => {
    if (!projectId || !isMountedRef.current) return;

    setLoadingProject(true);
    setFetchProjectError(null);

    BitmakerAPIClient.get(`/projects/${projectId}`, null, (response: any) => {
      if (!isMountedRef.current) return;

      const { isError, message } = handleAPIError(response);

      if (isError) {
        console.error("Error fetching project:", message);
        setFetchProjectError("Error fetching project. Please try again.");
        setLoadingProject(false);
        return;
      }

      setProject(EpicFromJSON(response.data));
      setLoadingProject(false);
    });
  }, [projectId, handleAPIError]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    const chartParam = new URLSearchParams(location.search).get("chart");

    if (isChartType(chartParam)) {
      setSelectedChart(chartParam);
      return;
    }

    setSelectedChart("timeStats");

    if (projectId && chartParam !== "timeStats") {
      navigate(`/projects/${projectId}?chart=timeStats`, { replace: true });
    }
  }, [location.search, projectId, navigate]);

  const handleChartChange = useCallback(
    (value: ChartType) => {
      if (!projectId) return;

      const { from, to } = getDefaultDateRange();

      setSelectedChart(value);
      setFromDate(from);
      setToDate(to);

      navigate(`/projects/${projectId}?chart=${value}`, { replace: true });
    },
    [projectId, navigate],
  );

  return {
    project,
    loadingProject,
    fetchProjectError,
    fromDate,
    toDate,
    selectedChart,
    handleChartChange,
    refetchProject: fetchProject,
  };
};
